import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { GlobalAnalysis } from "./useGlobalAnalysis";

interface Patient {
  id: string;
  name: string;
  cpf: string;
  birth_date: string | null;
  email: string | null;
  phone: string | null;
  gender: string | null;
  avatar_url?: string | null;
}

interface Appointment {
  id: string;
  patient_id: string;
  appointment_date: string;
  notes: string | null;
  created_at?: string;
}

interface Document {
  id: string;
  patient_id: string;
  file_name: string;
  file_type: string;
  file_path: string;
  extracted_text?: string | null;
  ai_summary?: string | null;
  ai_insights?: any | null;
  ai_risk_level?: string | null;
  created_at: string;
  appointment_id?: string | null;
  source?: 'documents_onboarding' | 'smartfile_sessions';
  content_type?: string | null;
  professional_notes?: string | null;
  status?: string | null;
}

interface LabResult {
  id: string;
  patient_id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  source_type?: string;
}

interface PatientInsight {
  id: string;
  patient_id: string;
  summary: string;
  highlights: any;
  generated_at: string;
}

interface RegistroSaude {
  id: string;
  patient_id: string;
  type: string;
  title: string;
  content: string | null;
  risk: string;
  ai_summary: string | null;
  observed_at: string;
  created_at: string;
}

interface Alert {
  id: string;
  patient_id: string;
  severity: string;
  title: string;
  message: string;
  source: string;
  created_at: string;
}

interface Subcategory {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_insights: any;
  ai_summary: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

export function usePatientData(patientId: string | undefined) {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [labResults, setLabResults] = useState<LabResult[]>([]);
  const [insights, setInsights] = useState<PatientInsight[]>([]);
  const [registrosSaude, setRegistrosSaude] = useState<RegistroSaude[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [globalAnalysisCache, setGlobalAnalysisCache] = useState<GlobalAnalysis | null>(null);
  const [smartfileSessions, setSmartfileSessions] = useState<SmartfileSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState({
    appointments: true,
    documents: true,
    labResults: true,
    insights: true,
    registrosSaude: true,
    alerts: true,
  });

  // Load patient details
  useEffect(() => {
    if (patientId) {
      loadPatientDetails();
    }
  }, [patientId]);

  // Load lab results immediately (doesn't need patient object)
  useEffect(() => {
    console.log("🔍 useEffect labResults disparado", { patientId });
    if (patientId) {
      loadLabResults();
    }
  }, [patientId]);

  // Load global analysis cache after patient data is loaded
  useEffect(() => {
    if (patient?.id) {
      console.log("🔄 Disparando loadGlobalAnalysisCache para patient:", patient.id);
      loadGlobalAnalysisCache();
    }
  }, [patient?.id]);

  // Load additional data after patient is loaded
  useEffect(() => {
    if (patient) {
      loadAppointments();
      loadDocuments();
      loadInsights();
      loadRegistrosSaude();
      loadAlerts();
      loadSubcategories();
      loadSmartfileSessions();
    }
  }, [patient]);

  // Also load smartfile sessions when patientId changes (even if patient object not loaded yet)
  useEffect(() => {
    if (patientId) {
      loadSmartfileSessions();
    }
  }, [patientId]);

  const loadPatientDetails = async () => {
    try {
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", patientId)
        .single();

      if (error) throw error;

      setPatient({
        id: data.id,
        name: data.full_name,
        cpf: data.cpf,
        birth_date: data.data_nascimento,
        email: data.email,
        phone: data.celular,
        gender: data.sexo,
        avatar_url: data.avatar_url,
      });
    } catch (error) {
      console.error("Error loading patient:", error);
      toast.error("Erro ao carregar dados do paciente");
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .eq("patient_id", patientId)
        .order("appointment_date", { ascending: false });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, appointments: false }));
    }
  };

  const loadDocuments = async () => {
    try {
      // Carregar documents_onboarding
      const { data: docsData, error: docsError } = await supabase
        .from("documents_onboarding")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (docsError) throw docsError;

      // Carregar smartfile_sessions
      const { data: smartfileData, error: smartfileError } = await supabase
        .from("smartfile_sessions")
        .select("*")
        .eq("patient_id", patientId)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (smartfileError) throw smartfileError;

      // Combinar e normalizar os documentos
      const normalizedDocs = (docsData || []).map(doc => ({
        ...doc,
        source: 'documents_onboarding' as const
      }));

      const normalizedSmartfile = (smartfileData || []).map(session => ({
        id: session.id,
        patient_id: session.patient_id,
        file_name: session.file_name,
        file_type: session.file_type,
        file_path: session.file_path,
        extracted_text: session.extracted_text,
        ai_summary: session.ai_summary,
        ai_insights: session.ai_insights,
        ai_risk_level: null,
        created_at: session.created_at,
        appointment_id: null,
        source: 'smartfile_sessions' as const,
        content_type: session.content_type,
        professional_notes: session.professional_notes,
        status: session.status,
      }));

      // Combinar e ordenar por data
      const allDocuments = [...normalizedDocs, ...normalizedSmartfile].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setDocuments(allDocuments);
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, documents: false }));
    }
  };

  const loadLabResults = async () => {
    console.log("🔍 loadLabResults iniciado", { patientId });
    try {
      if (!patientId) {
        console.log("❌ patientId é null/undefined");
        setLabResults([]);
        setLoadingData((prev) => ({ ...prev, labResults: false }));
        return;
      }

      console.log("🔍 Executando query para patient_id:", patientId);
      const { data, error } = await supabase
        .from("lab_results_onboarding")
        .select("id, patient_id, test_name, value, unit, ref_low, ref_high, collected_at, created_at, subcategory_id, source_type")
        .eq("patient_id", patientId)
        .order("collected_at", { ascending: false });

      console.log("🔍 Resultado da query:", {
        success: !error,
        dataLength: data?.length || 0,
        error: error?.message,
        sampleData: data?.[0]
      });

      if (error) {
        console.error("❌ Error na query:", error);
        throw error;
      }

      console.log("✅ setLabResults com", data?.length, "resultados");
      setLabResults(data || []);
    } catch (error) {
      console.error("❌ Error loading lab results:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, labResults: false }));
    }
  };

  const loadInsights = async () => {
    try {
      if (!patientId) {
        setInsights([]);
        setLoadingData((prev) => ({ ...prev, insights: false }));
        return;
      }

      const { data, error } = await supabase
        .from("patient_insights")
        .select("*")
        .eq("patient_id", patientId)
        .order("generated_at", { ascending: false });

      if (error) throw error;
      setInsights(data || []);
    } catch (error) {
      console.error("Error loading insights:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, insights: false }));
    }
  };

  const loadRegistrosSaude = async () => {
    try {
      if (!patientId) {
        setRegistrosSaude([]);
        setLoadingData((prev) => ({ ...prev, registrosSaude: false }));
        return;
      }

      // ✅ REMOVIDO: patient_health_records e health_record_items não são mais usados
      const data: any[] = [];
      const error = null;

      // Mapear contagem de items
      const recordsWithCount = (data || []).map(record => ({
        ...record,
        items_count: record.items?.[0]?.count || 0
      }));

      setRegistrosSaude(recordsWithCount);
    } catch (error) {
      console.error("Error loading health records:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, registrosSaude: false }));
    }
  };

  const loadAlerts = async () => {
    try {
      if (!patientId) {
        setAlerts([]);
        setLoadingData((prev) => ({ ...prev, alerts: false }));
        return;
      }

      const { data, error } = await supabase
        .from("alerts_onboarding")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAlerts(data || []);
    } catch (error) {
      console.error("Error loading alerts:", error);
    } finally {
      setLoadingData((prev) => ({ ...prev, alerts: false }));
    }
  };

  const loadSubcategories = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("lab_subcategories")
        .select(
          `
          id,
          name,
          category_id,
          category:lab_categories(name)
        `
        )
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      setSubcategories(
        (data || []).map((sub: any) => ({
          id: sub.id,
          name: sub.name,
          category_id: sub.category_id,
          category_name: sub.category?.name || "Outras",
        }))
      );
    } catch (error) {
      console.error("Error loading subcategories:", error);
    }
  };

  const loadGlobalAnalysisCache = async () => {
    try {
      if (!patientId) {
        console.log("⚠️ loadGlobalAnalysisCache: patientId não disponível");
        setGlobalAnalysisCache(null);
        return;
      }

      console.log("🔍 Buscando análise global para patient_id:", patientId);

      const { data, error } = await supabase
        .from("ai_global_analysis_cache")
        .select("json_result, generated_at")
        .eq("patient_id", patientId)
        .maybeSingle();

      if (error) {
        console.error("❌ Erro ao buscar análise global:", error);
        setGlobalAnalysisCache(null);
        return;
      }

      if (!data?.json_result) {
        console.log("ℹ️ Nenhuma análise global encontrada no cache");
        setGlobalAnalysisCache(null);
        return;
      }

      const analysisData = data.json_result as GlobalAnalysis;
      console.log("✅ Análise global carregada com sucesso:", {
        generated_at: data.generated_at,
        has_summary: !!analysisData?.summary,
        summary_length: analysisData?.summary?.length,
        highlights_count: analysisData?.highlights?.length,
        sections_count: analysisData?.sections?.length,
        problems_count: analysisData?.problems?.length || 0,
        medications_count: analysisData?.medications?.length || 0,
        problems: analysisData?.problems,
        medications: analysisData?.medications
      });

      setGlobalAnalysisCache(data.json_result as GlobalAnalysis);
    } catch (error) {
      console.error("❌ Erro inesperado ao carregar análise global:", error);
      setGlobalAnalysisCache(null);
    }
  };

  const loadSmartfileSessions = async () => {
    try {
      if (!patientId) {
        setSmartfileSessions([]);
        return;
      }

      const { data, error } = await supabase
        .from('smartfile_sessions')
        .select('id, file_name, content_type, ai_insights, ai_summary, file_path, extracted_text, created_at, structured_records, patient_id, status')
        .eq('patient_id', patientId)
        .not('structured_records', 'is', null)
        .neq('content_type', 'other') // ✅ Excluir documentos do tipo "other"
        .order('created_at', { ascending: false });

      if (error) {
        console.error("❌ Error loading smartfile sessions:", error);
        throw error;
      }

      console.log(`🔍 [loadSmartfileSessions] Found ${data?.length || 0} sessions with structured_records for patient ${patientId}`);

      // Converter structured_records de Json para array
      const sessions = (data || []).map(session => {
        let structuredRecords: any[] | null = null;

        if (session.structured_records) {
          if (Array.isArray(session.structured_records)) {
            structuredRecords = session.structured_records;
          } else if (typeof session.structured_records === 'string') {
            try {
              const parsed = JSON.parse(session.structured_records);
              structuredRecords = Array.isArray(parsed) ? parsed : null;
            } catch (e) {
              console.error("❌ Error parsing structured_records:", e);
              structuredRecords = null;
            }
          } else {
            // Se for um objeto, tentar converter para array ou descartar
            structuredRecords = null;
          }
        }

        const labResultsCount = structuredRecords && Array.isArray(structuredRecords)
          ? structuredRecords.filter((r: any) =>
            typeof r === 'object' && r !== null && 'type' in r && r.type === 'lab_result'
          ).length
          : 0;

        const trainingPlansCount = structuredRecords && Array.isArray(structuredRecords)
          ? structuredRecords.filter((r: any) =>
            typeof r === 'object' && r !== null && 'type' in r && r.type === 'training_plan'
          ).length
          : 0;

        console.log(`📊 Session ${session.id}: ${labResultsCount} lab_results, ${trainingPlansCount} training_plans found`);

        return {
          ...session,
          structured_records: structuredRecords
        } as SmartfileSession;
      });

      console.log(`✅ [loadSmartfileSessions] Loaded ${sessions.length} sessions`);
      setSmartfileSessions(sessions);
    } catch (error) {
      console.error("❌ Error loading smartfile sessions:", error);
      setSmartfileSessions([]);
    }
  };

  const refetch = () => {
    console.log("🔄 refetch chamado, patient:", patient?.id);
    if (patient) {
      loadAppointments();
      loadDocuments();
      loadLabResults();
      loadInsights();
      loadRegistrosSaude();
      loadAlerts();
      loadSubcategories();
      loadGlobalAnalysisCache();
      loadSmartfileSessions();
    }
  };

  return {
    patient,
    appointments,
    documents,
    labResults,
    insights,
    registrosSaude,
    alerts,
    subcategories,
    globalAnalysisCache,
    smartfileSessions,
    loading,
    loadingData,
    refetch,
    setGlobalAnalysisCache,
  };
}
